import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { catchError, map, Observable, of, startWith } from 'rxjs';
import { filterSuccessResult } from '@ngneat/query';
import { ActivityEntityName, EntityAdvancedFilterConditionLoose, EntityAdvancedFilterGroup, EntityAdvancedFilterLogicalOperator, EntityAdvancedFilterNode, EntityAdvancedFilterOperator, EntityAdvancedFilterOperatorOption, getEntityFilterOperators, getMetadataEntityFieldType, MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldDomain, MetadataEntityFieldType, MetadataEntityFilterType, MetadataEntityRecordScope, MetadataLookupTypeData, PickerTagRequest, FiltersRelativeDateUtility, FiltersNumberUtility, toPickerTagRequest } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ContentModel, ContentToken } from '@workbuddy/ui/v2/components/primitives/content';
import { FilterEditorType, FilterFieldDef, FilterFieldProvider, FilterFieldType, FilterGroup, FilterOperator, FilterOperatorDef, FilterOption, FilterOptionLoader, FilterRule } from '@workbuddy/ui/v2/blocks/filter';
import { metadataEntityFieldTypeIcons } from '@workbuddy/ui/pipes';
import { pluralize } from 'inflected';
import '@workbuddy/ui/utilities/english.inflections';
import { booleanFilterFormat, booleanFilterOptions, booleanFilterValueLabel } from './entity.filter.boolean.format';
import { effectiveEntityFieldType, isCustomEntityField, resolveEntityFieldPickerSpec, resolveEntityFieldStaticOptions } from './entity.field.value.set';

// ── Static maps ──────────────────────────────────────────────────────

const FIELD_TYPE_MAP: Partial<Record<MetadataEntityFieldType, FilterFieldType>> = {
  [MetadataEntityFieldType.String]: FilterFieldType.Text,
  [MetadataEntityFieldType.StringAlpha]: FilterFieldType.Text,
  [MetadataEntityFieldType.StringAlphaNumeric]: FilterFieldType.Text,
  [MetadataEntityFieldType.Number]: FilterFieldType.Number,
  [MetadataEntityFieldType.Currency]: FilterFieldType.Currency,
  [MetadataEntityFieldType.Percentage]: FilterFieldType.Percentage,
  [MetadataEntityFieldType.Boolean]: FilterFieldType.Boolean,
  [MetadataEntityFieldType.Date]: FilterFieldType.Date,
  [MetadataEntityFieldType.DateTime]: FilterFieldType.DateTime,
  [MetadataEntityFieldType.Time]: FilterFieldType.Time,
  [MetadataEntityFieldType.Enum]: FilterFieldType.Enum,
  [MetadataEntityFieldType.Flag]: FilterFieldType.Flag,
  [MetadataEntityFieldType.Tag]: FilterFieldType.Tag,
  [MetadataEntityFieldType.Lookup]: FilterFieldType.Lookup,
  [MetadataEntityFieldType.Parent]: FilterFieldType.Parent,
};

// EntityAdvancedFilterOperator and FilterOperator now use identical lowercase values — direct cast is safe
const toFilterOperator = (op: EntityAdvancedFilterOperator): FilterOperator => op as unknown as FilterOperator;

const CONTACT_ENTITIES = new Set<string>([ActivityEntityName.Account, ActivityEntityName.Customer, ActivityEntityName.Contact, ActivityEntityName.Employee, ActivityEntityName.Contractor, ActivityEntityName.BasicContact, ActivityEntityName.LoginContact, ActivityEntityName.ContactEmployee, ActivityEntityName.WorkBuddyCompany, ActivityEntityName.Company, ActivityEntityName.Resource]);

// Only person entities get avatars — matches wb-value behavior
const PERSON_ENTITIES = new Set<string>([ActivityEntityName.ContactEmployee, ActivityEntityName.Resource, ActivityEntityName.Contractor, ActivityEntityName.InvoiceContact]);

const NO_VALUE_OPERATORS = new Set<FilterOperator>([FilterOperator.IsEmpty, FilterOperator.IsNotEmpty]);

// Server compares textFilterable fields by normalised tokens for Contains/NotContains only —
// other text operators (Equals, StartsWith, ...) would compare against tokens inconsistently.
const TOKEN_MATCH_OPERATORS = new Set<FilterOperator>([FilterOperator.Contains, FilterOperator.NotContains, FilterOperator.IsEmpty, FilterOperator.IsNotEmpty]);

// ── Provider ─────────────────────────────────────────────────────────

@Injectable()
export class EntityFilterFieldProvider extends FilterFieldProvider {

  private readonly entityService = inject(EntityDataService);

  private readonly $entity = signal<ActivityEntityName | null>(null);
  private readonly $metadataFields = signal<MetadataEntityFieldData[]>([]);
  private readonly $fieldMap = computed(() => new Map(this.$metadataFields().map(f => [f.path ?? f.name, f])));

  /**
   * Stable field-def list. Must be a fixed signal instance — recreating `computed()` inside
   * `getFields()` each call breaks reactive consumers (add-filter menu never re-filters).
   */
  private readonly $fieldDefs = computed<FilterFieldDef[]>(() => {

    const fields = this.$metadataFields();
    const nameCounts = new Map<string, number>();
    for (const f of fields) {
      const name = f.displayName ?? f.name;
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }

    const seen = new Set<string>();
    return fields
      .map(f => this.toFieldDef(f, nameCounts.get(f.displayName ?? f.name) > 1))
      .filter((f): f is FilterFieldDef => {
        if (!f || seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  /**
   * Configure with a pre-resolved field list. Callers are responsible for
   * any field shaping (scope filtering, hiding domain-specific fields,
   * etc.) before passing them in. The provider applies a final `filterable`
   * + `!hidden` filter as a safety net.
   */
  public configureFields(entity: ActivityEntityName | null, fields: MetadataEntityFieldData[]): void {

    this.$entity.set(entity);
    this.$metadataFields.set((fields ?? []).filter((x: any) => !x.hidden && x.filterable));
  }

  // ── FilterFieldProvider ────────────────────────────────────────────

  public override getFields(): Signal<FilterFieldDef[]> {

    return this.$fieldDefs;
  }

  public override getOperators(fieldId: string): FilterOperatorDef[] {

    const field = this.$fieldMap().get(fieldId);
    if (!field) return [];

    const fieldType = this.effectiveFieldType(field);
    const allOps = getEntityFilterOperators(this.effectiveField(field)).map(op => this.toOperatorDef(op)).filter((o): o is FilterOperatorDef => o !== null);

    if (field.textFilterable) return allOps.filter(o => TOKEN_MATCH_OPERATORS.has(o.id));

    // Array-only fields: only In, NotIn, IsEmpty, IsNotEmpty
    switch (fieldType) {
      case MetadataEntityFieldType.Enum:
      case MetadataEntityFieldType.Tag:
      case MetadataEntityFieldType.Lookup:
      case MetadataEntityFieldType.Parent:
        return allOps.filter(o => o.id === FilterOperator.In || o.id === FilterOperator.NotIn || o.id === FilterOperator.IsEmpty || o.id === FilterOperator.IsNotEmpty);
      default:
        return allOps;
    }
  }

  public override getOptionLoader(fieldId: string): FilterOptionLoader {

    const field = this.$fieldMap().get(fieldId);
    const entity = this.$entity();
    if (!field || !entity) return { type: 'static', options: [] };

    const fieldType = this.effectiveFieldType(field);
    const isContact = this.isContactLookup(field, fieldType);
    const isPerson = this.isPersonLookup(field, fieldType);
    const showColor = fieldType === MetadataEntityFieldType.Tag || fieldType === MetadataEntityFieldType.Flag || fieldType === MetadataEntityFieldType.Enum || field.visualType === 'color' || field.visualType === 'status';

    switch (fieldType) {
      case MetadataEntityFieldType.Enum: {
        // Custom-field dropdowns carry no colour; first-class enums do, so only the latter show swatches.
        const options = resolveEntityFieldStaticOptions(field) ?? [];
        if (this.isCustomField(field)) return { type: 'static', options };
        return { type: 'static', showColor, options };
      }
      case MetadataEntityFieldType.Flag:
        return { type: 'static', showColor, options: resolveEntityFieldStaticOptions(field) ?? [] };
      case MetadataEntityFieldType.Boolean:
        return { type: 'static', options: booleanFilterOptions(booleanFilterFormat(entity, field)) };
      case MetadataEntityFieldType.Tag:
        return { type: 'picker', showColor, load: (query?) => this.loadPicker(ActivityEntityName.Tag, toPickerTagRequest(field.typeData, { search: query, includeSystemTags: true }) as PickerTagRequest, isContact) };
      case MetadataEntityFieldType.String:
        if (field?.filter?.type === MetadataEntityFilterType.AutoComplete && !field.virtual) {
          return { type: 'autocomplete', editorType: FilterEditorType.Text, load: (query?) => this.entityService.getAutoCompletePicker(entity, field.name, { search: query }).result$.pipe(filterSuccessResult(), map(r => (r.data?.items ?? []).map((x: any) => ({ id: x._id, label: x.name ?? x._id, data: x._id })))) };
        }
        return { type: 'static', editorType: FilterEditorType.Text };
      case MetadataEntityFieldType.Lookup:
      case MetadataEntityFieldType.Parent: {
        const spec = resolveEntityFieldPickerSpec(field, entity);
        if (!spec) return { type: 'static', options: [] };

        const groupSelectable = field.visualType === 'status';
        const lookupType = typeof field.typeData === 'string' ? field.typeData : (field.typeData as MetadataLookupTypeData)?.type;
        const emptyMessage = lookupType === ActivityEntityName.EntityStatus
          ? (field.name === 'subStatus' ? 'No sub-statuses configured' : 'No statuses configured')
          : 'No options';
        return { type: 'picker', showAvatar: isPerson, showColor, groupSelectable, emptyMessage, load: (query?) => this.loadPicker(spec.entity, spec.buildRequest(query), isPerson) };
      }
      case MetadataEntityFieldType.Date:
      case MetadataEntityFieldType.DateTime: {
        const filterType = field.filter?.type;
        const opts = field.filter?.options as any;
        if (filterType === MetadataEntityFilterType.Calendar) {
          return { type: 'static', editorType: FilterEditorType.Calendar, editorOptions: { showTime: opts?.showTime, timezone: opts?.timezone, minDate: opts?.minDate, maxDate: opts?.maxDate } };
        }
        return { type: 'static', editorType: FilterEditorType.RelativeDate, editorOptions: { mode: opts?.mode ?? 'date', direction: opts?.direction ?? 'all', timezone: opts?.timezone } };
      }
      case MetadataEntityFieldType.Time:
        return { type: 'static', editorType: FilterEditorType.Time, editorOptions: { timezone: (field.filter?.options as any)?.timezone } };
      case MetadataEntityFieldType.Number:
      case MetadataEntityFieldType.Currency:
      case MetadataEntityFieldType.Percentage:
        return { type: 'static', editorType: FilterEditorType.Number };
      default:
        return { type: 'static', options: [] };
    }
  }

  private loadPicker(entity: ActivityEntityName, request: any, isContact: boolean) {

    return this.entityService.getPicker(entity, request).result$.pipe(filterSuccessResult(), map(r => {
      const bindGroup = r.data?.bindGroup;
      // Tag multi-type pickers group by label type (Phase / Task / …) when bindGroup is unset.
      const groupByTagType = entity === ActivityEntityName.Tag && !!(request?.types?.length > 1);
      return (r.data?.items ?? []).map((x: any) => ({
        id: x._id,
        label: x.name,
        avatar: isContact ? x.name : null,
        color: x.color ?? null,
        group: bindGroup ? x[bindGroup] : (groupByTagType ? x.type : undefined),
        data: x,
      }) as FilterOption);
    }));
  }

  public override getValueDisplay(fieldId: string, value: unknown): ContentModel {

    const field = this.$fieldMap().get(fieldId);
    if (!field || value == null) return { label: null, mode: 'item', labelMode: 'text' };

    const fieldType = this.effectiveFieldType(field);
    const displayType = this.getDisplayType(field, fieldType);

    switch (displayType) {
      case 'tag': {
        const items = Array.isArray(value) ? value : [value];
        const mapped = items.map((v: any) => ({ name: this.itemName(v), color: this.itemColor(v) }));
        if (mapped.length === 1) return { label: mapped[0].name, color: { value: mapped[0].color, tooltip: mapped[0].name } as ContentToken, colorShape: 'square', mode: 'item', labelMode: 'text' };
        return { label: `${mapped.length} ${pluralize(field.displayName ?? field.name).toLowerCase()}`, color: mapped.map(v => ({ value: v.color, tooltip: v.name }) as ContentToken), colorShape: 'square', mode: 'item', labelMode: 'text' };
      }
      case 'flag': {
        const resolved = this.resolveDisplayValue(field, fieldType, value);
        const name = this.itemName(resolved);
        const color = this.itemColor(resolved);
        return { label: name, color: { value: color, tooltip: name } as ContentToken, mode: 'item', labelMode: 'text' };
      }
      case 'contact': {
        const items = Array.isArray(value) ? value : [value];
        const mapped = items.map((v: any) => ({ name: this.itemName(v) }));
        const isPerson = this.isPersonLookup(field, fieldType);
        const avatars = isPerson ? mapped.map(v => ({ value: v.name, tooltip: v.name }) as ContentToken) : undefined;
        if (mapped.length === 1) return { label: mapped[0].name, avatar: avatars, mode: 'item', labelMode: 'text' };
        return { label: `${mapped.length} ${pluralize(field.displayName ?? field.name).toLowerCase()}`, avatar: avatars, mode: 'item', labelMode: 'text' };
      }
      case 'enum': {
        const items = Array.isArray(value) ? value : [value];
        const mapped = items.map((v: any) => {
          const resolved = this.resolveDisplayValue(field, fieldType, v);
          return { name: this.itemName(resolved), color: this.itemColor(resolved) };
        });
        const hasColor = mapped.some(v => v.color && v.color !== '#ccc');
        if (mapped.length === 1) return { label: mapped[0].name, color: hasColor ? { value: mapped[0].color, tooltip: mapped[0].name } as ContentToken : undefined, mode: 'item', labelMode: 'text' };
        return { label: `${mapped.length} ${pluralize(field.displayName ?? field.name).toLowerCase()}`, color: hasColor ? mapped.map(v => ({ value: v.color, tooltip: v.name }) as ContentToken) : undefined, mode: 'item', labelMode: 'text' };
      }
      default:
        return { label: this.getValueLabel(field, fieldType, value), mode: 'item', labelMode: 'text' };
    }
  }

  public override getValueDisplay$(fieldId: string, value: unknown): Observable<ContentModel> {

    const fallback = this.getValueDisplay(fieldId, value);
    const field = this.$fieldMap().get(fieldId);
    if (!field || this.effectiveFieldType(field) !== MetadataEntityFieldType.Tag) return of(fallback);

    const items = Array.isArray(value) ? value : [value];
    const missingIds = [...new Set(items
      .filter(item => !this.hasItemColor(item))
      .map(item => this.itemId(item))
      .filter((id): id is string => !!id))];
    if (!missingIds.length) return of(fallback);

    const request = {
      ...toPickerTagRequest(field.typeData, { includeSystemTags: true }),
      filters: [{ _id: '_id', value: missingIds }],
    } as PickerTagRequest;

    return this.loadPicker(ActivityEntityName.Tag, request, false).pipe(
      map(options => {
        const resolvedById = new Map(options.map(option => [option.id, option.data ?? option]));
        const resolved = items.map(item => resolvedById.get(this.itemId(item)) ?? item);
        return this.getValueDisplay(fieldId, Array.isArray(value) ? resolved : resolved[0]);
      }),
      startWith(fallback),
      catchError(() => of(fallback)),
    );
  }

  public override toFilterGroup(domainState: unknown): FilterGroup | null {

    const group = domainState as FilterGroup | null;
    return group?.rules?.length ? group : null;
  }

  public override fromFilterGroup(group: FilterGroup | null): EntityAdvancedFilterGroup | null {

    return group?.rules?.length ? group as unknown as EntityAdvancedFilterGroup : null;
  }

  // ── Field mapping ──────────────────────────────────────────────────

  /**
   * Resolves the effective first-class field type. Custom fields carry the generic CustomField type;
   * their real type lives in typeData.type — map it so the field maps through FIELD_TYPE_MAP and gets
   * the correct operators/editor. Unmapped custom-field types (Address/Image) return CustomField, which
   * has no FIELD_TYPE_MAP entry, so they are correctly excluded from the filter dropdown.
   */
  private effectiveFieldType(field: MetadataEntityFieldData): MetadataEntityFieldType {

    return effectiveEntityFieldType(field);
  }

  /** Field shim presenting the effective type so model utilities (operators) resolve custom fields correctly. */
  private effectiveField(field: MetadataEntityFieldData): MetadataEntityFieldData {

    const type = this.effectiveFieldType(field);
    return type === getMetadataEntityFieldType(field) ? field : { ...field, type };
  }

  private isCustomField(field: MetadataEntityFieldData): boolean {

    return isCustomEntityField(field);
  }

  /**
   * Custom fields show their group ("Stage · Notes") only where two levels could carry the same name.
   * The domain stamps `Any` on single-level entities, where the group is just the entity name and adds nothing.
   */
  private isScopedCustomField(field: MetadataEntityFieldData): boolean {

    return this.isCustomField(field) && field.scope != null && field.scope !== MetadataEntityRecordScope.Any;
  }

  private toFieldDef(field: MetadataEntityFieldData, useQualifiedName = false): FilterFieldDef | null {

    const fieldType = this.effectiveFieldType(field);
    const type = FIELD_TYPE_MAP[fieldType];
    if (!type) return null;

    let operators = getEntityFilterOperators(this.effectiveField(field)).map(op => toFilterOperator(op.value)).filter(Boolean);
    if (field.textFilterable) operators = operators.filter(o => TOKEN_MATCH_OPERATORS.has(o));
    const icon = field.icon ?? metadataEntityFieldTypeIcons[fieldType] ?? 'fa-question';
    const defaultOperator = this.getDefaultOperator(fieldType, operators);
    const useGroup = useQualifiedName || this.isScopedCustomField(field);
    const label = useGroup && field.qualifiedDisplayName ? field.qualifiedDisplayName : (field.displayName ?? field.name);

    return { id: field.path ?? field.name, label, icon: `fa-regular ${icon}`, type, operators, defaultOperator };
  }

  private toOperatorDef(op: EntityAdvancedFilterOperatorOption): FilterOperatorDef | null {

    const id = toFilterOperator(op.value);
    if (!id) return null;
    return { id, label: op.label, requiresValue: !NO_VALUE_OPERATORS.has(id) };
  }

  private getDefaultOperator(fieldType: MetadataEntityFieldType, operators: FilterOperator[]): FilterOperator {

    // textFilterable fields fall through to `default: operators[0]` — ENTITY_FILTER_TEXT_OPERATORS
    // orders Contains before NotContains/IsEmpty/IsNotEmpty, so no dedicated case is needed here.
    switch (fieldType) {
      case MetadataEntityFieldType.Tag:
      case MetadataEntityFieldType.Lookup:
      case MetadataEntityFieldType.Parent:
      case MetadataEntityFieldType.Enum:
        return operators.includes(FilterOperator.In) ? FilterOperator.In : operators[0];
      case MetadataEntityFieldType.Date:
      case MetadataEntityFieldType.DateTime:
        return operators.includes(FilterOperator.RelativeDate) ? FilterOperator.RelativeDate : operators[0];
      default:
        return operators[0] ?? FilterOperator.Equals;
    }
  }

  // ── Option loading ─────────────────────────────────────────────────

  private getLoaderType(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType): 'static' | 'picker' | 'autocomplete' {

    switch (fieldType) {
      case MetadataEntityFieldType.Enum:
      case MetadataEntityFieldType.Flag:
      case MetadataEntityFieldType.Boolean:
        return 'static';
      case MetadataEntityFieldType.String:
        return field?.filter?.type === MetadataEntityFilterType.AutoComplete ? 'autocomplete' : 'static';
      default:
        return 'picker';
    }
  }

  private isContactLookup(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType): boolean {

    if (fieldType !== MetadataEntityFieldType.Lookup && fieldType !== MetadataEntityFieldType.Parent) return false;
    const lookupType = typeof field.typeData === 'string' ? field.typeData : (field.typeData as MetadataLookupTypeData)?.type;
    return CONTACT_ENTITIES.has(lookupType);
  }

  private isPersonLookup(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType): boolean {

    if (fieldType !== MetadataEntityFieldType.Lookup && fieldType !== MetadataEntityFieldType.Parent) return false;
    const lookupType = typeof field.typeData === 'string' ? field.typeData : (field.typeData as MetadataLookupTypeData)?.type;
    return PERSON_ENTITIES.has(lookupType);
  }

  private toFilterOption(item: any, isContact: boolean): FilterOption {

    return { id: item.value?._id ?? item.value ?? item.label, label: item.label ?? '', avatar: isContact ? item.avatar ?? item.label : null, color: item.color ?? null, data: item.value };
  }

  // ── Value display ──────────────────────────────────────────────────

  private getDisplayType(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType): 'tag' | 'flag' | 'contact' | 'enum' | 'default' {

    switch (fieldType) {
      case MetadataEntityFieldType.Tag: return 'tag';
      case MetadataEntityFieldType.Flag: return 'flag';
      case MetadataEntityFieldType.Enum: return 'enum';
      case MetadataEntityFieldType.Lookup:
      case MetadataEntityFieldType.Parent:
        if (field.visualType === 'status' || field.visualType === 'color') return 'enum';
        return this.isContactLookup(field, fieldType) ? 'contact' : 'default';
      default: return 'default';
    }
  }

  private getValueLabel(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType, value: unknown): string | null {

    switch (fieldType) {
      case MetadataEntityFieldType.String:
      case MetadataEntityFieldType.StringAlpha:
      case MetadataEntityFieldType.StringAlphaNumeric:
      case MetadataEntityFieldType.Parent:
      case MetadataEntityFieldType.Lookup: {
        if (!Array.isArray(value)) return this.itemName(value);
        if (!value.length) return null;
        const names = value.map((x: any) => this.itemName(x)).filter(Boolean);
        return names.length > 3 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
      }
      case MetadataEntityFieldType.Boolean:
        return booleanFilterValueLabel(value, booleanFilterFormat(this.$entity(), field));
      case MetadataEntityFieldType.Time:
      case MetadataEntityFieldType.DateTime:
      case MetadataEntityFieldType.Date:
        return FiltersRelativeDateUtility.getLabel(value as any) ?? `${value}`;
      case MetadataEntityFieldType.Currency:
      case MetadataEntityFieldType.Percentage:
      case MetadataEntityFieldType.Number:
        if (this.isRangeValue(value)) return this.getRangeLabel(value);
        if (typeof value !== 'object') return `${value}`;
        return field?.filter?.type === MetadataEntityFilterType.Number ? FiltersNumberUtility.getLabel(value as any) : `${value}`;
      default:
        return `${value}`;
    }
  }

  private itemName(v: any): string {

    if (!v) return '';
    if (typeof v === 'string') return v;
    return v.name ?? v.label ?? v.key ?? `${v}`;
  }

  private isRangeValue(value: unknown): value is { from?: unknown; to?: unknown } {

    return !!value && typeof value === 'object' && ('from' in value || 'to' in value);
  }

  private getRangeLabel(value: { from?: unknown; to?: unknown }): string | null {

    if (value.from != null && value.to != null) return `${value.from} - ${value.to}`;
    if (value.from != null) return `>= ${value.from}`;
    if (value.to != null) return `<= ${value.to}`;
    return null;
  }

  private itemColor(v: any): string {

    if (!v) return '#ccc';
    if (typeof v.color === 'string') return v.color;
    if (v.color?.value) return v.color.value;
    return '#ccc';
  }

  private hasItemColor(v: any): boolean {

    return typeof v?.color === 'string' || typeof v?.color?.value === 'string';
  }

  private itemId(v: any): string | null {

    if (typeof v === 'string') return v;
    return v?._id ?? v?.id ?? null;
  }

  private resolveDisplayValue(field: MetadataEntityFieldData, fieldType: MetadataEntityFieldType, value: unknown): unknown {

    if (value == null || typeof value === 'object') return value;

    switch (fieldType) {
      case MetadataEntityFieldType.Enum:
      case MetadataEntityFieldType.Flag: {
        const options = Array.isArray(field.typeData) ? field.typeData as Array<Record<string, unknown>> : [];
        return options.find(item => item['value'] === value || item['key'] === value) ?? value;
      }
      default:
        return value;
    }
  }

}
